import type { UnitActivity } from "@/lib/api/courseUnits";

export const ACTIVITY_DETAIL_MAX = 500;
export type DraftActivity = { id: string; label: string; minutes: string; detail: string };
export const draftActivities = (activities: UnitActivity[] = []): DraftActivity[] => activities.map(activity => ({
  id: activity.id, label: activity.label,
  minutes: activity.minutes == null ? "" : String(activity.minutes), detail: activity.detail ?? "",
}));
export const savedActivities = (activities: DraftActivity[]): UnitActivity[] => activities.map(activity => ({
  id: activity.id, label: activity.label.trim(), minutes: activity.minutes === "" ? null : Number(activity.minutes),
  detail: activity.detail.trim() || null,
}));
export const activitiesValid = (activities: DraftActivity[]) => activities.length <= 40 && activities.every(activity =>
  !!activity.label.trim() && activity.label.length <= 200 && activity.detail.trim().length <= ACTIVITY_DETAIL_MAX
  && (activity.minutes === "" || (/^\d+$/.test(activity.minutes) && Number(activity.minutes) <= 1440)));

export function planTiming(activities: UnitActivity[], startsAt?: string | null, endsAt?: string | null) {
  const planned = activities.reduce((total, activity) => total + (activity.minutes ?? 0), 0);
  const untimed = activities.filter(activity => activity.minutes == null).length;
  const duration = startsAt && endsAt ? (Date.parse(endsAt) - Date.parse(startsAt)) / 60000 : NaN;
  const budget = Number.isFinite(duration) && duration >= 0 ? duration : null;
  return { planned, untimed, budget, remaining: budget === null ? null : budget - planned };
}

/** A draft row's minutes, or null when untimed (blank or not a whole number). */
export const draftMinutes = (activity: DraftActivity) => /^\d+$/.test(activity.minutes) ? Number(activity.minutes) : null;

/**
 * The one timing figure for a plan's activities, read wherever the plan is
 * summarized (the Running order and the Class overview), so no two totals
 * compete. Only activity minutes count: due dates and reading estimates are
 * not class time. Without a scheduled length there is no budget to measure
 * against, and the figure says so rather than inventing one.
 */
export function planTimingFigure(activities: DraftActivity[], startsAt: string | null, endsAt: string | null, clock = true) {
  const timing = planTiming(activities.map(activity => ({ id: activity.id, label: activity.label, minutes: draftMinutes(activity) })), startsAt, endsAt);
  const over = timing.remaining !== null && timing.remaining < 0 ? -timing.remaining : 0;
  const left = timing.remaining !== null && timing.remaining > 0 ? timing.remaining : 0;
  const measured = !clock ? `${timing.planned} min planned`
    : timing.budget === null ? `${timing.planned} min planned, session length unavailable`
      : `${timing.planned} of ${timing.budget} min${over ? `, ${over} over` : left ? `, ${left} left` : ""}`;
  return { ...timing, over, left, text: `${measured}${timing.untimed ? `, ${timing.untimed} untimed` : ""}` };
}
